// TrueChops as a platform: a site built on it is customized in JavaScript, by plugins listed in
// truechops.config.js. A plugin is { id, name, setup(api), server?(api) } and, with the api, can:
//   - turn whole apps on or off (config `apps`: score, daw, map, reels, community, modules…; or
//     api.setApp and api.onlyApps), and say how the site looks (api.brand: name, logo, colour, styles)
//   - listen to what happens (api.on: "score.changed", "route.changed", "map.selected"…) and tell
//     others (api.emit)
//   - change values on their way (api.filter: "nav.items", "score.toolbar", "techniques.list",
//     "post.types"… each filter gets the value and returns it changed)
//   - put elements in at insertion points (api.slot("score.toolbar.end", Component)), and take out,
//     replace, rename, reorder or move the site's named parts (api.hide("score.toolbar.find"),
//     api.replace("score.title", C), api.rename("score.toolbar.save", "Keep"),
//     api.arrange("score.toolbar", [...names]), api.move("score.toolbar.find", "score.top"))
//   - change the menu (api.filter("nav.places", places => places): add, rename, reorder or take away
//     places and their tabs, or api.navItem({ label, href }) for a link down the rail)
//   - add whole pages (api.page("/students/:id", Component), shown at /x/<plugin>/students/42)
//   - override named functions (api.override("similarity.compare", (original) => (...args) => …))
//   - add data types (api.type: something besides rhythms, with how to show and check it), post types,
//     techniques for Score bars and DAW clips (api.technique, api.clipTool), plugin effects for the
//     DAW (api.audioEffect: Tone.js nodes, or JavaScript on the audio thread), assistant actions
//     (api.action), and server endpoints (in server(api): api.endpoint("GET", "/hello", handler),
//     served at /api/x/<plugin>/hello)
// This file is the registry itself: plain JavaScript, the same in the browser and on the server.
// components/platform/react.js gives React its parts (<Slot>, <Part>, useFilter…).

export const APPS = { score: "Score", daw: "DAW", map: "Rhythm Graph", reels: "Reels", community: "Community", grow: "Grow", studio: "Studio", modules: "Modules",
  practice: "Practice", vault: "Vault", byos: "BYOS", room: "Melody Room", tools: "Tools", book: "Book pages", assistant: "Assistant",
  store: "Store", extensions: "Extensions", school: "School", lessons: "Lessons", marching: "Band" };
// Which app each page belongs to (by its first path segment), so a page of an app that's off says so.
export const APP_OF_PATH = { "": "score", daw: "daw", synth: "daw", graph: "map", world: "map", tune: "map", reels: "reels", community: "community", discover: "community", feed: "community", u: "community", r: "community",
  grow: "grow", studio: "studio", modules: "modules", practice: "practice", sessions: "practice", vault: "vault", byos: "byos", room: "room", tools: "tools", book: "book",
  store: "store", read: "store", extensions: "extensions", mods: "extensions", school: "school", lessons: "lessons", band: "marching" };

const byOrder = (a, b) => a.order - b.order || a.at - b.at;

export function createPlatform(config = {}) {
  const apps = { ...Object.fromEntries(Object.keys(APPS).map((id) => [id, true])), ...(config.apps || {}) };
  const listeners = new Map(), filters = new Map(), slots = new Map(), hidden = new Map(), replaced = new Map(), overrides = new Map(), renamed = new Map(), moved = new Map(), arranged = new Map();
  const types = new Map(), postTypes = new Map(), techniques = new Map(), clipTools = new Map(), audioEffects = new Map(), actions = new Map(), pages = [], endpoints = [], errors = [];
  const installed = new Set(), appChanges = new Map(), looks = new Map();
  let plugins = [];
  let at = 0, version = 0;
  const changed = new Set();
  const bump = () => { version += 1; for (const fn of changed) { try { fn(version); } catch { /* a listener's problem */ } } };
  const add = (map, key, item) => { (map.get(key) || map.set(key, []).get(key)).push({ ...item, at: at++ }); map.get(key).sort(byOrder); };
  // Whether an app is on: as the config says, then as each plugin has changed it.
  function appOn(id) {
    let on = apps[id] !== false;
    for (const changes of appChanges.values()) if (id in changes) on = changes[id];
    return on;
  }
  // How the site looks: each plugin's say, the last one on top.
  const branding = () => Object.assign({}, ...[...looks.values()].map((look) => Object.fromEntries(Object.entries(look).filter(([, value]) => value))));
  const named = (value, what) => { if (typeof value !== "string" || !/^[a-z][\w.:-]{0,79}$/i.test(value)) throw new Error(`${what} needs a name like "score.toolbar".`); return value; };

  // The api a plugin's setup gets: everything it adds is marked as its own.
  function apiFor(plugin) {
    const own = (item) => ({ ...item, plugin: plugin.id });
    return {
      plugin: { id: plugin.id, name: plugin.name },
      config: Object.freeze({ ...config, plugins: undefined }),
      appEnabled: appOn,
      setApp(id, on) { (appChanges.get(plugin.id) || appChanges.set(plugin.id, {}).get(plugin.id))[id] = Boolean(on); bump(); },
      // These apps, and no others.
      onlyApps(ids) { for (const id of Object.keys(APPS)) this.setApp(id, ids.includes(id)); },
      // { name, logo, home (where the logo goes), accent (a colour), css (styles of its own) }
      brand(look) { looks.set(plugin.id, look); bump(); },
      on(event, handler) { add(listeners, named(event, "An event"), own({ handler, order: 0 })); return () => { const list = listeners.get(event) || []; listeners.set(event, list.filter((item) => item.handler !== handler)); }; },
      emit: (event, payload) => emit(event, payload),
      filter(name, fn, { order = 10 } = {}) { add(filters, named(name, "A filter"), own({ fn, order })); bump(); },
      slot(name, Component, { order = 10, props = {} } = {}) { add(slots, named(name, "A slot"), own({ Component, order, props })); bump(); },
      hide(name) { hidden.set(named(name, "A part"), own({})); bump(); },
      replace(name, Component) { replaced.set(named(name, "A part"), own({ Component })); bump(); },
      // A named part with other words on it.
      rename(name, label) { renamed.set(named(name, "A part"), own({ label: String(label) })); bump(); },
      // A named part taken to another area of the same page (areas are in src/platform/parts.js).
      move(name, area) { moved.set(named(name, "A part"), own({ area: named(area, "An area") })); bump(); },
      // An area's parts in this order; the ones left out follow, as they were.
      arrange(area, names) { arranged.set(named(area, "An area"), own({ names: names.map((name) => named(name, "A part")) })); bump(); },
      override(name, wrap) { add(overrides, named(name, "A function"), own({ wrap, order: 10 })); bump(); },
      type(name, definition) { types.set(named(name, "A type"), own({ name, ...definition })); bump(); },
      postType(name, definition) { postTypes.set(named(name, "A post type"), own({ name, ...definition })); bump(); },
      technique(definition) { techniques.set(named(definition?.id, "A technique"), own({ kinds: ["kit", "marching", "piano", "melody"], icon: "star", ...definition })); bump(); },
      clipTool(definition) { clipTools.set(named(definition?.id, "A clip tool"), own(definition)); bump(); },
      // A DAW plugin effect (src/lib/daw/plugins.js): { id, name, group, params, make(Tone, settings, tempo),
      // set(node, settings, tempo) }, or written for the audio thread: { id, name, knobs, code }.
      audioEffect(definition) { audioEffects.set(named(definition?.id, "An audio effect"), own({ group: "This site's", ...definition })); bump(); },
      action(definition) { actions.set(named(definition?.name, "An action"), own({ args: {}, ...definition })); bump(); },
      // A link down the menu's rail: { label, href, who ("signedIn", "signedOut", "admin") }.
      navItem(item) { this.filter("nav.items", (items) => [...items, { id: item.href, ...item }]); },
      // A page of the plugin's own, at /x/<plugin>/<path> (`:name` parts are params).
      // { title, props: given to the page as well }
      page(path, Component, options = {}) { pages.push(own({ path: String(path).replace(/^\/+|\/+$/g, ""), Component, ...options })); bump(); },
      // Whether the person here has added this official extension (src/lib/extensions).
      installed: (id = plugin.id) => installed.has(id),
      endpoint(method, path, handler) { endpoints.push(own({ method: String(method).toUpperCase(), path: String(path).replace(/^\/+|\/+$/g, ""), handler })); },
    };
  }

  function emit(event, payload) {
    for (const { handler, plugin } of listeners.get(event) || []) {
      try { handler(payload); } catch (error) { errors.push({ plugin, where: event, message: error.message }); }
    }
  }
  function applyFilters(name, value, context = {}) {
    let current = value;
    for (const { fn, plugin } of filters.get(name) || []) {
      try { const next = fn(current, context); if (next !== undefined) current = next; } catch (error) { errors.push({ plugin, where: name, message: error.message }); }
    }
    return current;
  }
  // A named function, as plugins have wrapped it (each wrap gets the one before it).
  function callable(name, original) {
    return (overrides.get(name) || []).reduce((fn, { wrap, plugin }) => {
      try { const wrapped = wrap(fn); return typeof wrapped === "function" ? wrapped : fn; } catch (error) { errors.push({ plugin, where: name, message: error.message }); return fn; }
    }, original);
  }

  // Where a path is among a list of { path, plugin }: { item, params } or null.
  function pathIn(list, pluginId, path) {
    const parts = String(path || "").split("/").filter(Boolean);
    for (const item of list) {
      if (item.plugin !== pluginId) continue;
      const pattern = item.path.split("/").filter(Boolean);
      if (pattern.length !== parts.length) continue;
      const params = {};
      if (pattern.every((piece, index) => piece.startsWith(":") ? (params[piece.slice(1)] = decodeURIComponent(parts[index]), true) : piece === parts[index])) return { item, params };
    }
    return null;
  }

  const platform = {
    get name() { return branding().name || config.name || "TrueChops"; },
    apps,
    appEnabled: appOn,
    branding,
    appOfPath(pathname = "/") { return APP_OF_PATH[String(pathname).split("/")[1] || ""] || null; },
    use(plugin, { server = false } = {}) {
      if (!plugin?.id) throw new Error("A plugin needs an id.");
      if (plugins.some((item) => item.id === plugin.id)) return platform;
      plugins.push({ id: plugin.id, name: plugin.name || plugin.id, version: plugin.version || null });
      const api = apiFor(plugin);
      try { if (server) plugin.server?.(api); else plugin.setup?.(api); } catch (error) { errors.push({ plugin: plugin.id, where: server ? "server" : "setup", message: error.message }); }
      bump();
      return platform;
    },
    // Everything a plugin added, gone (an extension turned off, or the site's settings changed).
    remove(id) {
      plugins = plugins.filter((item) => item.id !== id);
      appChanges.delete(id); looks.delete(id);
      for (const list of [listeners, filters, slots, overrides]) for (const [key, items] of list) list.set(key, items.filter((item) => item.plugin !== id));
      for (const list of [hidden, replaced, renamed, moved, arranged, types, postTypes, techniques, clipTools, audioEffects, actions]) for (const [key, item] of list) if (item.plugin === id) list.delete(key);
      for (const list of [pages, endpoints]) for (let index = list.length - 1; index >= 0; index--) if (list[index].plugin === id) list.splice(index, 1);
      bump();
      return platform;
    },
    setInstalled(ids) { installed.clear(); for (const id of ids) installed.add(id); bump(); },
    isInstalled: (id) => installed.has(id),
    plugins: () => [...plugins],
    emit, applyFilters, callable,
    slotted: (name) => (slots.get(name) || []).map(({ Component, props, plugin }) => ({ Component, props, plugin })),
    isHidden: (name) => hidden.has(name),
    replacement: (name) => replaced.get(name)?.Component || null,
    label: (name) => renamed.get(name)?.label ?? null,
    movedTo: (name) => moved.get(name)?.area || null,
    movedInto: (area) => [...moved].filter(([, item]) => item.area === area).map(([name]) => name),
    arrangement: (area) => arranged.get(area)?.names || [],
    types: () => [...types.values()], type: (name) => types.get(name) || null,
    postTypes: () => [...postTypes.values()], postType: (name) => postTypes.get(name) || null,
    techniques: () => [...techniques.values()], technique: (id) => techniques.get(id) || null,
    clipTools: () => [...clipTools.values()],
    audioEffects: () => [...audioEffects.values()],
    actions: () => [...actions.values()],
    endpoints: () => [...endpoints],
    pages: () => [...pages],
    // A plugin's page for a path: { Component, params, title, props } or null.
    pageFor(pluginId, path) {
      const found = pathIn(pages, pluginId, path);
      return found && { ...found.item, params: found.params };
    },
    // Where a plugin's endpoint for a request is: { handler, params } or null.
    endpointFor(method, pluginId, path) {
      const found = pathIn(endpoints.filter((item) => item.method === String(method).toUpperCase()), pluginId, path);
      return found && { handler: found.item.handler, params: found.params };
    },
    errors: () => [...errors],
    version: () => version,
    subscribe(fn) { changed.add(fn); return () => changed.delete(fn); },
  };
  for (const plugin of config.plugins || []) platform.use(plugin);
  return platform;
}
